import { useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useMcpCoverage } from '../hooks/use-mcp';
import type { McpCoverage } from '../types';
import { CoverageRoutesTable } from './coverage-routes-table';

/** Фильтр по признаку «стал инструментом / исключён». */
const ALL = '__all__';
const TOOLS_ONLY = '__tools__';

interface SummaryTileProps {
  label: string;
  value: number;
}

function SummaryTile({ label, value }: SummaryTileProps) {
  return (
    <div className="rounded-lg border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}

function CoverageSummary({ coverage }: { coverage: McpCoverage }) {
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <SummaryTile label="Роутов всего" value={coverage.totalRoutes} />
        <SummaryTile label="Инструментов" value={coverage.tools} />
        <SummaryTile label="С проверкой прав" value={coverage.withPermission} />
        <SummaryTile label="С описанием" value={coverage.withSummary} />
        <SummaryTile label="Тело из ручного разбора" value={coverage.bodyFromManualParse} />
        <SummaryTile label="Без схемы тела" value={coverage.missingBodySchema} />
      </div>
      {Object.keys(coverage.excluded).length > 0 ? (
        <p className="text-xs text-muted-foreground">
          Исключено:{' '}
          {Object.entries(coverage.excluded)
            .map(([reason, count]) => `${reason} — ${count}`)
            .join(', ')}
        </p>
      ) : null}
    </div>
  );
}

/** Вкладка «Покрытие API»: сводка и таблица роутов с фильтрами. */
export function CoveragePanel({ enabled }: { enabled: boolean }) {
  const { data, isLoading, error } = useMcpCoverage(enabled);
  const [search, setSearch] = useState('');
  const [excludedFilter, setExcludedFilter] = useState(ALL);
  const [bodyFilter, setBodyFilter] = useState(ALL);

  const routes = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (data?.routes ?? []).filter((route) => {
      if (needle && !route.path.toLowerCase().includes(needle)) {
        return false;
      }
      if (excludedFilter === TOOLS_ONLY && route.toolName === null) {
        return false;
      }
      if (excludedFilter !== ALL && excludedFilter !== TOOLS_ONLY && route.excluded !== excludedFilter) {
        return false;
      }
      return bodyFilter === ALL || route.bodySource === bodyFilter;
    });
  }, [data, search, excludedFilter, bodyFilter]);

  if (isLoading) {
    return <Skeleton className="h-64 w-full rounded-xl" />;
  }
  if (error || !data) {
    return <p className="py-8 text-center text-sm text-destructive">{error?.message ?? 'Нет данных'}</p>;
  }

  return (
    <div className="space-y-4">
      <CoverageSummary coverage={data} />
      <div className="flex flex-wrap gap-2">
        <Input
          className="w-full sm:w-72"
          placeholder="Поиск по пути"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Select value={excludedFilter} onValueChange={setExcludedFilter}>
          <SelectTrigger className="w-full sm:w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Все роуты</SelectItem>
            <SelectItem value={TOOLS_ONLY}>Только инструменты</SelectItem>
            {Object.keys(data.excluded).map((reason) => (
              <SelectItem key={reason} value={reason}>
                Исключён: {reason}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={bodyFilter} onValueChange={setBodyFilter}>
          <SelectTrigger className="w-full sm:w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Любая схема тела</SelectItem>
            <SelectItem value="route">Тело: схема роута</SelectItem>
            <SelectItem value="manual-parse">Тело: ручной разбор</SelectItem>
            <SelectItem value="none">Тело: нет схемы</SelectItem>
          </SelectContent>
        </Select>
        <span className="self-center text-xs text-muted-foreground">
          Показано {routes.length} из {data.routes.length}
        </span>
      </div>
      <CoverageRoutesTable routes={routes} />
    </div>
  );
}
